/**
 * D11 «Гроші» (owner, 22.10.2026; mockups/analytics.html): over the 30 days
 * of «Енергія» the facts — «Витрати на електрику» (bought × the hour price of
 * the client's tariff); objects with PV also «Економія від СЕС» (produced −
 * exported × the hour price). Under each the formula on the client's
 * numbers. Without a tariff, history or DAM prices — one line instead. The
 * tariff is the one the sums were counted by (`money.tariff_type`); the
 * rates come from the object.
 *
 * Owner, 25.10.2026: the model — «З внутрішнім арбітражем, розрахунок» (the
 * same days through the day-program planner) and «Різниця» — is at the bottom
 * of «Внутрішній арбітраж», beside «Хочу керувати» (`MoneyModel`); control
 * off — «Якби Phage керувала батареєю — модель», «Можлива економія». A fixed
 * tariff has no arbitrage: one line instead.
 */
import type { CSSProperties } from 'react';
import type { SiteMoneyResponse, SiteResponse } from 'shared';

import { fmtDate, fmtDayMonth, fmtNum, fmtNumShort } from '../../../components/format.js';
import { ZONES, type ZoneName, isZone, ratesFromCells } from '../../../components/tariff.js';
import { useLocale } from '../../../i18n/locale-context.js';
import type { MessageKey } from '../../../i18n/messages.js';

export function MoneyCard({
  money,
  site,
}: {
  money: SiteMoneyResponse | undefined;
  site: Pick<SiteResponse, 'tariff_type' | 'zone_rates_per_kwh' | 'has_pv'>;
}) {
  const { t, tf, tp, locale } = useLocale();
  const fmt = (v: number, d = 2) => fmtNum(locale, v, d);
  const tariffType = money?.tariff_type ?? site.tariff_type;
  const fixed = tariffType === 'fixed';
  const zone = isZone(tariffType) ? tariffType : null;
  const cells = site.zone_rates_per_kwh;
  const rates = zone && cells ? ratesFromCells(zone, cells) : null;
  const flatRate = fixed && cells ? (ratesFromCells('fixed', cells)?.flat ?? null) : null;
  // Zones dearest first, as the formula reads.
  const zones: ZoneName[] = zone
    ? [...ZONES[zone]].sort((a, b) => (rates?.[b] ?? 0) - (rates?.[a] ?? 0))
    : [];
  const zoneOfRate = (rate: number) => zones.find((z) => Math.abs((rates?.[z] ?? Number.NaN) - rate) < 1e-6);

  const ok = money?.status === 'ok' && money.from && money.to;
  const days = ok ? tp('site.money.days', money.days) : '';
  let sub: string | null = null;
  if (ok && money.from && money.to) {
    const tariff =
      zone && rates
        ? tf('site.money.tariff.zone', {
            name: t(`site.tariff.${zone}` as MessageKey),
            rates: zones
              .map((z) => `${t(`site.money.zone.${z}` as MessageKey)} ${fmt(rates[z] ?? 0)}`)
              .join(', '),
          })
        : flatRate !== null
          ? tf('site.money.tariff.zone', { name: t('site.tariff.fixed'), rates: fmt(flatRate) })
          : t(`site.tariff.${tariffType ?? 'none'}` as MessageKey);
    sub = tf('site.money.sub', { days, from: fmtDayMonth(money.from), to: fmtDate(money.to), tariff });
  }

  const value = (v: number | null) => <MoneyValue v={v} />;
  /** «77,7 кВт·год вдень × 6,50 + 38,8 кВт·год вночі × 3,25». */
  const ratesParts = (byRate: readonly { rate: number; kwh: number }[]) =>
    byRate
      .map((r) => {
        const z = zoneOfRate(r.rate);
        return tf('site.money.how.part', {
          kwh: fmt(r.kwh, 1),
          zone: z ? t(`site.money.at.${z}` as MessageKey) : '',
          rate: fmt(r.rate),
        }).replace('  ', ' ');
      })
      .join(' + ');
  let actualHow = '';
  if (ok && money.import_kwh !== null) {
    if (money.avg_price !== null) {
      actualHow = tf('site.money.how.dam', { kwh: fmt(money.import_kwh, 1), price: fmt(money.avg_price) });
    } else if (money.by_rate.length > 0) {
      actualHow = tf('site.money.how.zones', { parts: ratesParts(money.by_rate) });
    }
  }
  const pv = site.has_pv ? (money?.pv ?? null) : null;
  let pvHow = '';
  if (pv) {
    const used = tf('site.money.how.pv', {
      produced: fmt(pv.produced_kwh, 1),
      exported: fmt(pv.exported_kwh, 1),
      self: fmt(pv.self_kwh, 1),
    });
    // No rate (a fixed tariff without «Ставка»): the kWh only.
    const price =
      pv.saving === null
        ? ''
        : pv.avg_price !== null
          ? tf('site.money.how.pv_dam', { price: fmt(pv.avg_price) })
          : pv.by_rate.length === 1
            ? ` × ${fmt(pv.by_rate[0]?.rate ?? 0)}`
            : pv.by_rate.length > 1
              ? `: ${ratesParts(pv.by_rate)}`
              : '';
    pvHow = used + price;
  }
  const n = 1 + (pv ? 1 : 0);

  return (
    <section className="card" aria-labelledby="money-title">
      <div className="card-head">
        <h2 id="money-title">{t('site.money.title')}</h2>
        {sub ? <span className="sub num">{sub}</span> : null}
      </div>
      {ok ? (
        <>
          {/* Phone: the first tile spans the row, an odd one left over spans it too. */}
          <div className={n % 2 === 0 ? 'tiles odd' : 'tiles'} style={{ '--n': String(n) } as CSSProperties}>
            <div className="tile">
              <span className="label">{t('site.money.actual')}</span>
              {value(money.actual)}
              {actualHow ? <span className="sub num">{actualHow}</span> : null}
            </div>
            {pv ? (
              <div className="tile">
                <span className="label">{t('site.money.pv')}</span>
                {value(pv.saving)}
                <span className="sub num">{pvHow}</span>
              </div>
            ) : null}
          </div>
          {money.restored_hours > 0 ? (
            <p className="hint">{tf('site.data.restored', { hours: money.restored_hours })}</p>
          ) : null}
        </>
      ) : money ? (
        <p className="muted">
          {t(
            money.status === 'no_tariff'
              ? 'site.money.no_tariff'
              : money.status === 'no_prices'
                ? 'site.money.no_prices'
                : 'site.money.no_history',
          )}
        </p>
      ) : null}
    </section>
  );
}

/** A sum in ₴; null — no rate for it. */
function MoneyValue({ v }: { v: number | null }) {
  const { t, locale } = useLocale();
  return v === null ? (
    <span className="dir">{t('site.money.no_rate')}</span>
  ) : (
    <span className="value num">
      {fmtNum(locale, v, 2)}
      <small>₴</small>
    </span>
  );
}

/**
 * The model of «Гроші» at the bottom of «Внутрішній арбітраж» (owner,
 * 25.10.2026): the same days through the day-program planner, within the
 * installer's limits, and the difference with what was paid; control off —
 * a model of what Phage would do. A fixed tariff — one line.
 */
export function MoneyModel({
  money,
  control,
  site,
}: {
  money: SiteMoneyResponse | undefined;
  control: boolean;
  site: SiteResponse;
}) {
  const { t, tf, tp, locale } = useLocale();
  if (money?.status !== 'ok' || !money.from || !money.to) return null;
  if (money.tariff_type === 'fixed') return <p className="hint">{t('site.money.fixed')}</p>;
  const days = tp('site.money.days', money.days);
  // «Як рахується» (owner, 26.10.2026): the battery and the installer's limits the model took.
  const n = (v: number) => fmtNumShort(locale, v, 2);
  const floor = site.max_reserve
    ? site.soc_max_pct
    : Math.min(Math.max(site.soc_min_pct, site.reserve_pct), site.soc_max_pct);
  const lim = site.inverter_settings.limits;
  const grid = lim.grid_charge_kw ?? lim.power_kw;
  const battery = tf(grid > 0 ? 'site.arb.model.how.battery' : 'site.arb.model.how.battery_no_grid', {
    capacity: n(site.capacity_kwh ?? 0),
    floor: n(floor),
    max: n(site.soc_max_pct),
    etaCharge: n(site.eta_charge),
    etaDischarge: n(site.eta_discharge),
    power: n(lim.power_kw),
    grid: n(grid),
  });
  return (
    <div data-testid="money-model">
      <div className="chart-title">
        {tf('site.arb.model.title', { days, from: fmtDayMonth(money.from), to: fmtDate(money.to) })}
      </div>
      <div className="tiles odd" style={{ '--n': '2' } as CSSProperties}>
        <div className="tile">
          <span className="label">{t(control ? 'site.money.arb' : 'site.money.arb.model')}</span>
          <MoneyValue v={money.with_arbitrage} />
          <span className="sub">{tf('site.money.how.arb', { days })}</span>
        </div>
        <div className="tile">
          <span className="label">{t(control ? 'site.money.diff' : 'site.money.diff.model')}</span>
          <MoneyValue v={money.difference} />
          <span className="sub">{t('site.money.how.diff')}</span>
        </div>
      </div>
      <details open>
        <summary>{t('site.arb.model.how')}</summary>
        <ol className="how">
          <li>{t('site.arb.model.how.load')}</li>
          <li>{battery}</li>
          <li>{t('site.arb.model.how.edges')}</li>
        </ol>
      </details>
      {control ? null : <p className="hint">{t('site.money.model.note')}</p>}
    </div>
  );
}
